import { type BreadcrumbItem, type SharedData, type Startsite } from '@/types';
import { Form, Head, router, usePage } from '@inertiajs/react';

import Heading from '@/components/heading';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';

import SiteController from '@/actions/App/Http/Controllers/SiteController';
import InputError from '@/components/input-error';
import { ButtonGroup, ButtonGroupText } from '@/components/ui/button-group';
import { Input } from '@/components/ui/input';
import {
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
} from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import AppLayout from '@/layouts/app-layout';
import { home, login } from '@/routes';
import { notice } from '@/routes/verification';
import { ArrowLeft, Check, EyeIcon } from 'lucide-react';
import { useState } from 'react';

const breadcrumbs: BreadcrumbItem[] = [
    {
        title: 'Create Site',
        href: home().url,
    },
];

export default function Create({
    startsites = [],
}: {
    startsites: Startsite[];
}) {
    const { auth } = usePage<SharedData>().props;
    const [selectedStartsite, setSelectedStartsite] = useState<
        Startsite | undefined
    >(() => {
        if (typeof window === 'undefined' || !auth.user?.email_verified_at) {
            return undefined;
        }

        const slug = new URLSearchParams(window.location.search).get(
            'startsite',
        );

        return startsites.find((startsite) => startsite.slug === slug);
    });

    const selectStartsite = (startsite: Startsite | undefined) => {
        if (!auth.user) {
            const redirectTarget = startsite
                ? home({ query: { startsite: startsite.slug } }).url
                : home().url;

            router.visit(login({ query: { redirect: redirectTarget } }));
            return;
        }

        if (!auth.user.email_verified_at) {
            router.visit(notice());
            return;
        }

        setSelectedStartsite(startsite);
    };

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title="Create Site" />
            <div className="px-4 py-6">
                <Heading
                    title="Create a website"
                    description="Start with a blank canvas or choose a template"
                />

                {selectedStartsite ? (
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => selectStartsite(undefined)}
                            className="mb-4"
                        >
                            <ArrowLeft />
                            {selectedStartsite.name}
                        </Button>

                        <Form
                            {...SiteController.store.form()}
                            options={{
                                preserveScroll: true,
                            }}
                            className="space-y-4"
                        >
                            {({ processing, errors }) => (
                                <>
                                    <div className="grid w-full max-w-sm">
                                        <ButtonGroup>
                                            <ButtonGroupText asChild>
                                                <Label htmlFor="name">
                                                    https://
                                                </Label>
                                            </ButtonGroupText>
                                            <InputGroup>
                                                <InputGroupInput
                                                    id="name"
                                                    name="name"
                                                    autoFocus
                                                    placeholder="yourwebsite"
                                                />
                                                <InputGroupAddon align="inline-end"></InputGroupAddon>
                                            </InputGroup>
                                            <ButtonGroupText>
                                                .berta.me
                                            </ButtonGroupText>
                                        </ButtonGroup>

                                        <InputError
                                            className="mt-2"
                                            message={errors.name}
                                        />

                                        <InputError
                                            className="mt-2"
                                            message={errors.startsite}
                                        />
                                    </div>

                                    <p className="text-sm">
                                        By creating a website, you agree to the{' '}
                                        <a
                                            href="https://berta.me/terms-of-service/"
                                            target="_blank"
                                            className="underline"
                                        >
                                            Terms of Service
                                        </a>
                                    </p>

                                    <Input
                                        type="hidden"
                                        name="startsite"
                                        value={selectedStartsite.slug}
                                    />

                                    <Button disabled={processing}>
                                        Create
                                        {processing && <Spinner />}
                                    </Button>

                                    <p className="max-w-sm text-sm">
                                        Your Berta.me trial will last for 14
                                        days. During this period you can build
                                        your website, add your own domain name
                                        and more. You may upgrade to one of our
                                        hosting plans at any time!
                                    </p>
                                </>
                            )}
                        </Form>
                    </>
                ) : (
                    <div className="grid grid-cols-2 gap-4 sm:flex sm:flex-wrap">
                        {startsites.map((startsite) => (
                            <Card key={startsite.slug} className="sm:w-3xs">
                                <CardHeader>
                                    <CardTitle className="flex gap-3 text-sm">
                                        {startsite.name}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="flex flex-1 items-center justify-center">
                                    {startsite.url && (
                                        <img
                                            src={`/images/startsites/${startsite.slug}.jpg`}
                                        />
                                    )}
                                </CardContent>
                                <CardFooter className="flex flex-col items-start gap-4 sm:flex-row sm:justify-between">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() =>
                                            selectStartsite(startsite)
                                        }
                                    >
                                        <Check />
                                        Choose
                                    </Button>

                                    {startsite.url && (
                                        <Button
                                            asChild
                                            variant="secondary"
                                            size="sm"
                                        >
                                            <a
                                                href={startsite.url}
                                                target="_blank"
                                            >
                                                <EyeIcon />
                                                Preview
                                            </a>
                                        </Button>
                                    )}
                                </CardFooter>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </AppLayout>
    );
}
